import React, { useState, useEffect, useMemo } from 'react';
import { Loader2, ShoppingCart, FileText, Wrench } from 'lucide-react';
import { useTheme } from '../../../core/ui/ThemeContext';
import { useLanguage } from '../../../core/i18n/LanguageContext';
import { addLagerCartItem } from 'core/api/apiService';
import MissingFieldsModal from '../../MissingFieldsModal';

/**
 * SchnellBanfBlock — Schnell-BANF Panel mit Lieferantenauswahl aus Infosaetzen (Phase 3a).
 *
 * Props:
 *   data                  Object   getLagerMaterialDetail-Ergebnis
 *   werk                  string
 *   user                  Object
 *   suppliers             Array|null   aus getLagerSuppliers
 *   suppliersLoading      boolean
 *   showSuccess           (msg) => void
 *   showError             (msg) => void
 *   onCartUpdated         () => void
 *   onNavigateToCreateBanf (prefill) => void
 */
const SchnellBanfBlock = ({
  data,
  werk,
  user,
  suppliers,
  suppliersLoading,
  showSuccess,
  showError,
  onCartUpdated,
  onNavigateToCreateBanf,
}) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();

  const mat = data?.material ?? {};

  const todayStr = new Date().toISOString().split('T')[0];
  const defaultDelivery = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return d.toISOString().split('T')[0];
  })();

  const [menge, setMenge] = useState('1');
  const [lieferdatum, setLieferdatum] = useState(defaultDelivery);
  const [selectedSupplierKey, setSelectedSupplierKey] = useState('ohne');
  const [banfSaving, setBanfSaving] = useState(false);
  // Kontierungs-Dialog ("Position ergänzen") vor der Übernahme in die BANF
  const [showKontierung, setShowKontierung] = useState(false);

  // Wenn Lieferanten geladen: guenstigsten vorauswaehlen + Lieferdatum anpassen
  useEffect(() => {
    if (!Array.isArray(suppliers) || suppliers.length === 0) return;
    const cheapest = suppliers.find((s) => s.is_cheapest);
    const first = cheapest ?? suppliers[0];
    if (first?.lifnr) {
      setSelectedSupplierKey(`lifnr:${first.lifnr}`);
      if (first.planned_delivery_time != null) {
        const d = new Date();
        d.setDate(d.getDate() + first.planned_delivery_time);
        setLieferdatum(d.toISOString().split('T')[0]);
      }
    }
  }, [suppliers]);

  const selectedSupplier = useMemo(() => {
    if (!Array.isArray(suppliers) || !selectedSupplierKey || selectedSupplierKey === 'ohne') return null;
    const lifnr = selectedSupplierKey.replace('lifnr:', '');
    return suppliers.find((s) => s.lifnr === lifnr) ?? null;
  }, [suppliers, selectedSupplierKey]);

  // "Position ergänzen"-Dialog erwartet ein product-Objekt; aus den Lager-Daten
  // ableiten (Werk vorbefüllt, Wunschlieferant aus der Auswahl, Kontierung leer).
  const kontierungProduct = useMemo(() => {
    const vendor = selectedSupplier
      ? (selectedSupplier.lieferant_name ?? selectedSupplier.lifnr ?? '')
      : (selectedSupplierKey === 'letzter' ? (data?.letzter_lieferant ?? '') : '');
    return {
      name: mat.maktx || mat.matnr || '',
      description: mat.maktx || mat.matnr || '',
      plant: werk || '',
      cost_center: '',
      purchasing_org: '',
      purchasing_group: '',
      preferred_vendor: vendor,
      sources: [],
    };
  }, [mat, werk, selectedSupplier, selectedSupplierKey, data]);

  const handleSupplierChange = (key) => {
    setSelectedSupplierKey(key);
    if (key === 'ohne') return;
    const lifnr = key.replace('lifnr:', '');
    const sup = Array.isArray(suppliers) ? suppliers.find((s) => s.lifnr === lifnr) : null;
    if (sup?.planned_delivery_time != null) {
      const d = new Date();
      d.setDate(d.getDate() + sup.planned_delivery_time);
      setLieferdatum(d.toISOString().split('T')[0]);
    }
  };

  const letzterPreis = data?.letzter_preis ?? null;
  const letzterPreisWaehrung = data?.letzter_preis_waehrung ?? 'EUR';
  const effectivePrice    = selectedSupplier?.price_per_unit ?? letzterPreis;
  const effectiveCurrency = selectedSupplier?.waehrung ?? letzterPreisWaehrung;

  const mengeNum = parseFloat(menge);
  const mengeValid = !Number.isNaN(mengeNum) && mengeNum > 0;
  const lieferdatumValid = !!lieferdatum && lieferdatum >= todayStr;

  const geschaetzterPreis = mengeValid && effectivePrice != null
    ? new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        .format(mengeNum * effectivePrice) + ' ' + effectiveCurrency
    : '—';

  const buildCartPayload = () => {
    // Lieferant bestimmen: aus Infosatz, ODER bei Fallback-Auswahl "letzter"
    // der zuletzt verwendete Lieferant (sonst ginge er still verloren), sonst leer.
    let wunschlieferant = '';
    if (selectedSupplier) {
      wunschlieferant = selectedSupplier.lieferant_name ?? selectedSupplier.lifnr ?? '';
    } else if (selectedSupplierKey === 'letzter') {
      wunschlieferant = data?.letzter_lieferant ?? '';
    }
    return {
      description: mat.maktx || mat.matnr || '',
      quantity: mengeNum,
      price: effectivePrice ?? 0,
      currency: effectiveCurrency,
      unit: mat.meins || 'ST',
      material: mat.matnr || '',
      material_group: mat.matkl || '',
      product_group: mat.matkl || '',
      preferred_vendor: wunschlieferant,
      vendor: wunschlieferant,
      plant: werk || '',
      delivery_date: lieferdatum,
      herkunft: 'LAGER_MODUL',
      image_url: '',
    };
  };

  // "In BANF übernehmen" öffnet zuerst den Kontierungs-Dialog ("Position ergänzen").
  const handleInBanf = () => {
    if (!mengeValid) { showError(t('lm.banfMengeRequired')); return; }
    if (!lieferdatumValid) { showError(t('lm.banfLieferdatumRequired')); return; }
    setShowKontierung(true);
  };

  // Nach dem Ausfüllen der Kontierung: Position mit Kontierungsdaten in den
  // Warenkorb/die BANF übernehmen. Backend erwartet einkaeuferorganisation
  // (statt purchasing_org) und cost_center; Sachkonto = gl_account.
  const handleKontierungConfirm = async (fields) => {
    setShowKontierung(false);
    setBanfSaving(true);
    try {
      const base = buildCartPayload();
      const vendor = fields.preferred_vendor || base.preferred_vendor || '';
      await addLagerCartItem(user, {
        ...base,
        preferred_vendor: vendor,
        vendor,
        cost_center: fields.cost_center || undefined,
        einkaueferorganisation: fields.purchasing_org || undefined,
        plant: fields.plant || werk || '',
        gl_account: fields.gl_account || undefined,
        sachkonto: fields.gl_account || undefined,
        kontierungstyp: fields.kontierungstyp || 'kostenstelle',
        einkauefergruppe: fields.einkauefergruppe || undefined,
        auftrag: fields.auftrag || undefined,
      });
      showSuccess(t('lm.banfAdded'));
      if (typeof onCartUpdated === 'function') onCartUpdated();
    } catch (err) {
      showError(err.message || t('lm.errorDetail'));
    } finally {
      setBanfSaving(false);
    }
  };

  // Platzhalter für die spätere PM-/Instandhaltungs-Integration (Auftragserstellung).
  const handleAuftragErstellen = () => {
    showSuccess(t('lm.auftragComingSoon'));
  };

  const handleZurDetailBanf = () => {
    if (!mengeValid) { showError(t('lm.banfMengeRequired')); return; }
    if (typeof onNavigateToCreateBanf === 'function') {
      onNavigateToCreateBanf(buildCartPayload());
    }
  };

  const cellMuted = isDarkMode ? 'text-white/45' : 'text-slate-400';
  const cellVal   = isDarkMode ? 'text-white/85' : 'text-slate-800';
  const sectionTitle = `text-xs font-semibold uppercase tracking-wide mb-3 ${isDarkMode ? 'text-white/50' : 'text-slate-400'}`;
  const inputCls = `w-full px-3 py-2 rounded-lg border text-sm outline-none transition-all ${isDarkMode
    ? 'bg-white/8 border-white/15 text-white placeholder-white/30 focus:border-brand/50'
    : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:border-brand/50 focus:ring-1 focus:ring-brand/20'
  }`;

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <ShoppingCart className={`w-4 h-4 ${isDarkMode ? 'text-brand/70' : 'text-brand'}`} />
        <p className={sectionTitle}>{t('lm.schnellBanf')}</p>
      </div>

      <div className="space-y-3">
        {/* Menge */}
        <div>
          <label className={`block text-xs mb-1 ${cellMuted}`}>
            {t('lm.banfMenge')}
            {mat.meins && <span className="ml-1 font-mono">{mat.meins}</span>}
            <span className={`ml-1 ${isDarkMode ? 'text-red-400/70' : 'text-red-500'}`}>*</span>
          </label>
          <input
            type="number"
            min="0.001"
            step={mat.meins === 'ST' || mat.meins === 'STK' ? '1' : '0.001'}
            value={menge}
            onChange={(e) => setMenge(e.target.value)}
            className={inputCls}
          />
        </div>

        {/* Lieferdatum */}
        <div>
          <label className={`block text-xs mb-1 ${cellMuted}`}>
            {t('lm.banfLieferdatum')}
            <span className={`ml-1 ${isDarkMode ? 'text-red-400/70' : 'text-red-500'}`}>*</span>
          </label>
          <input
            type="date"
            min={todayStr}
            value={lieferdatum}
            onChange={(e) => setLieferdatum(e.target.value)}
            className={inputCls}
          />
        </div>

        {/* Lieferantenauswahl aus Infosaetzen */}
        <div>
          <label className={`block text-xs mb-1 ${cellMuted}`}>{t('lm.banfLieferant')}</label>
          {suppliersLoading ? (
            <div className="flex items-center gap-1.5 py-1">
              <Loader2 className={`w-3 h-3 animate-spin ${isDarkMode ? 'text-brand/60' : 'text-brand/60'}`} />
              <span className={`text-xs ${cellMuted}`}>{t('lm.suppliersLoading')}</span>
            </div>
          ) : Array.isArray(suppliers) && suppliers.length > 0 ? (
            <select
              value={selectedSupplierKey}
              onChange={(e) => handleSupplierChange(e.target.value)}
              className={inputCls}
            >
              {suppliers.map((sup) => (
                <option key={sup.lifnr} value={`lifnr:${sup.lifnr}`}>
                  {sup.lieferant_name ?? sup.lifnr}
                  {' — '}
                  {new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                    .format(sup.price_per_unit)}&nbsp;{sup.waehrung}/{mat.meins || '—'}
                  {sup.planned_delivery_time != null
                    ? ` — ${t('lm.banfSupplierDelivery', { days: sup.planned_delivery_time })}`
                    : ''}
                </option>
              ))}
              <option value="ohne">{t('lm.banfOhneLieferant')}</option>
            </select>
          ) : (
            <select
              value={selectedSupplierKey}
              onChange={(e) => setSelectedSupplierKey(e.target.value)}
              className={inputCls}
            >
              {data?.letzter_lieferant && (
                <option value="letzter">{data.letzter_lieferant}</option>
              )}
              <option value="ohne">{t('lm.banfOhneLieferant')}</option>
            </select>
          )}
        </div>

        {/* Geschaetzter Preis */}
        <div>
          <p className={`text-xs ${cellMuted}`}>{t('lm.banfGeschaetzterPreis')}</p>
          <p className={`text-sm font-semibold mt-0.5 ${cellVal}`}>{geschaetzterPreis}</p>
        </div>

        {/* Buttons */}
        <div className="flex flex-col gap-2 pt-1">
          <button
            type="button"
            onClick={handleInBanf}
            disabled={banfSaving || !mengeValid || !lieferdatumValid}
            className={`flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-xl text-sm font-semibold transition-all
              ${isDarkMode
                ? 'bg-brand/80 hover:bg-brand text-white disabled:opacity-40 disabled:cursor-not-allowed'
                : 'bg-brand hover:bg-blue-700 text-white disabled:opacity-40 disabled:cursor-not-allowed'
              }`}
          >
            {banfSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingCart className="w-4 h-4" />}
            {t('lm.banfInBanf')}
          </button>
          <button
            type="button"
            onClick={handleZurDetailBanf}
            disabled={banfSaving || !mengeValid}
            className={`flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-xl text-sm font-medium transition-all border
              ${isDarkMode
                ? 'border-white/20 text-white/70 hover:border-brand/50 hover:text-white/90 disabled:opacity-40 disabled:cursor-not-allowed'
                : 'border-slate-300 text-slate-600 hover:border-brand/50 hover:text-brand disabled:opacity-40 disabled:cursor-not-allowed'
              }`}
          >
            <FileText className="w-4 h-4" />
            {t('lm.banfZurDetailBanf')}
          </button>
          {/* Auftrag erstellen — Platzhalter für die spätere PM-/Instandhaltungs-Integration */}
          <button
            type="button"
            onClick={handleAuftragErstellen}
            className={`flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-xl text-sm font-medium transition-all border
              ${isDarkMode
                ? 'border-white/20 text-white/70 hover:border-amber-400/50 hover:text-white/90'
                : 'border-slate-300 text-slate-600 hover:border-amber-500/50 hover:text-amber-600'
              }`}
          >
            <Wrench className="w-4 h-4" />
            {t('lm.banfAuftragErstellen')}
          </button>
        </div>
      </div>

      <MissingFieldsModal
        isOpen={showKontierung}
        onClose={() => setShowKontierung(false)}
        onConfirm={handleKontierungConfirm}
        product={kontierungProduct}
        dynamicKontierung={true}
        user={user}
      />
    </div>
  );
};

export default SchnellBanfBlock;
